import { useState, useEffect, useRef, useCallback } from "react";
import * as sync from "../lib/sync.js";
import { loadGis } from "../lib/gis.js";

/**
 * Optional Google sign-in and cross-device sync.
 *
 * Sync is deliberately manual: two explicit buttons, not a background mirror.
 * Farm records are hand-entered over weeks and a silent auto-sync that guessed
 * wrong would be very hard to notice and impossible to undo. See src/lib/sync.js.
 *
 * The Google script is loaded on demand rather than in index.html — nobody who
 * never opens Settings should pay for it, and it keeps the app's "no third-party
 * scripts" property true until the user opts in.
 */

const card = {
  background: "var(--bg-card)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: 16,
};

const btn = (tone = "neutral") => ({
  padding: "10px 14px",
  borderRadius: 9,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
  border:
    tone === "primary" ? "1px solid rgba(181,224,38,0.45)" : "1px solid rgba(255,255,255,0.15)",
  background: tone === "primary" ? "rgba(181,224,38,0.14)" : "rgba(255,255,255,0.06)",
  color: tone === "primary" ? "var(--active)" : "var(--text-main)",
});

export default function AccountPanel({ clientId, onToast, onDataReplaced }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(null); // 'in' | 'pull' | 'push' | null
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(null);
  const buttonRef = useRef(null);

  // Shared with the header via the sync bus, so signing in from either place
  // updates both without lifting state through App.jsx.
  useEffect(() => {
    const refresh = async () => {
      const signedIn = await sync.isSignedIn();
      setMeta(signedIn ? (await sync.getMeta()) || {} : null);
    };
    refresh();
    return sync.onAccountChange(refresh);
  }, []);

  const handleCredential = useCallback(
    async (response) => {
      setBusy("in");
      setError(null);
      try {
        const m = await sync.signIn(response.credential);
        setMeta(m);
        onToast?.(`Signed in as ${m.email || "your account"}`);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(null);
      }
    },
    [onToast]
  );

  // Render Google's own button. Their branding guidelines require it, and it is
  // the only supported way to get an ID token from the One Tap library.
  useEffect(() => {
    if (meta || !clientId || !buttonRef.current) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !buttonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "filled_black",
          size: "large",
          text: "signin_with",
          shape: "pill",
        });
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [meta, clientId, handleCredential]);

  async function doPull() {
    setBusy("pull");
    setError(null);
    try {
      const res = await sync.pull();
      if (!res.applied) {
        onToast?.("Nothing saved to this account yet");
      } else {
        onToast?.("Loaded from your account");
        onDataReplaced?.();
      }
    } catch (e) {
      setError(e.message);
      if (/expired/i.test(e.message)) setMeta(null);
    } finally {
      setBusy(null);
    }
  }

  async function doPush() {
    setBusy("push");
    setError(null);
    try {
      const res = await sync.push();
      if (res.conflict) {
        setConflict(res);
      } else {
        onToast?.("Saved to your account");
      }
    } catch (e) {
      setError(e.message);
      if (/expired/i.test(e.message)) setMeta(null);
    } finally {
      setBusy(null);
    }
  }

  async function resolveKeepServer() {
    await sync.acceptServer(conflict.serverData, conflict.serverUpdatedAt);
    setConflict(null);
    onToast?.("Kept the other device's version");
    onDataReplaced?.();
  }

  async function resolveKeepLocal() {
    try {
      await sync.overwriteServer(conflict.serverUpdatedAt);
      setConflict(null);
      onToast?.("Kept this device's version");
    } catch (e) {
      setError(e.message);
    }
  }

  async function doSignOut() {
    await sync.signOut();
    setMeta(null);
    setConflict(null);
    onToast?.("Signed out — your records stay on this device");
  }

  if (!clientId) {
    return (
      <div style={card}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Sync is not configured on this deployment.
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-main)", marginBottom: 4 }}>
        Account &amp; sync
      </div>
      <div style={{ fontSize: 12, color: "var(--text-soft)", lineHeight: 1.6, marginBottom: 14 }}>
        Optional. Sign in to copy your records between devices. Without an account
        everything stays on this device, exactly as it does now.
      </div>

      {!meta && (
        <>
          <div ref={buttonRef} style={{ minHeight: 44 }} />
          <div style={{ fontSize: 11, color: "#5f7d5f", marginTop: 10, lineHeight: 1.6 }}>
            Signing in is the point at which your records leave this device. See the{" "}
            <a href="/privacy" style={{ color: "var(--text-soft)" }}>
              Privacy Policy
            </a>
            .
          </div>
        </>
      )}

      {meta && !conflict && (
        <>
          <div style={{ fontSize: 13, color: "var(--text-soft2)", marginBottom: 12 }}>
            Signed in{meta.email ? ` as ${meta.email}` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={doPush} disabled={!!busy} style={btn("primary")}>
              {busy === "push" ? "Saving…" : "Save to account"}
            </button>
            <button onClick={doPull} disabled={!!busy} style={btn()}>
              {busy === "pull" ? "Loading…" : "Load from account"}
            </button>
            <button onClick={doSignOut} disabled={!!busy} style={btn()}>
              Sign out
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#5f7d5f", marginTop: 10, lineHeight: 1.6 }}>
            Sync runs only when you press a button — nothing uploads in the background.
          </div>
        </>
      )}

      {conflict && (
        <div
          style={{
            border: "1px solid rgba(240,160,32,0.4)",
            background: "rgba(240,160,32,0.08)",
            borderRadius: 10,
            padding: 14,
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#f0a020", marginBottom: 6 }}>
            ⚠ Another device saved first
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-soft2)", lineHeight: 1.6, marginBottom: 12 }}>
            Your account has changes made somewhere else since this device last
            synced. Choose which version to keep — the other one is discarded.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={resolveKeepLocal} style={btn("primary")}>
              Keep this device
            </button>
            <button onClick={resolveKeepServer} style={btn()}>
              Keep the other device
            </button>
            <button onClick={() => setConflict(null)} style={btn()}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: "#ef6b63", marginTop: 12, lineHeight: 1.5 }}>
          {error}
        </div>
      )}
    </div>
  );
}
